<template>
    <div class="summary">
        <v-card v-for="tile in tiles" :key="tile.title" outlined :class="['summary-tile', { 'dashboard-alert': tile.alert }]">
            <div class="d-flex align-center">
                <v-icon :color="tile.alert ? 'white' : 'primary'" class="mr-2">{{ tile.icon }}</v-icon>
                <span class="caption text-uppercase">{{ tile.title }}</span>
            </div>
            <div class="text-h4 font-weight-bold mt-1">{{ tile.value }}</div>
            <div class="caption summary-secondary">{{ tile.subtitle }}</div>
        </v-card>
    </div>
</template>

<script>
import PaymentStatus from "@/enum/PaymentStatus";
import UnlockBy from "@/enum/UnlockBy";
import { money } from "@/helpers/paymentAlerts";

import { historyPaymentAlerts, isPaid, sessionPaymentAlerts } from "./shared/alerts";

import format from "./shared/format";

export default {
    name: "DailySummary",

    props: {
        sessions: { type: Array, default: () => [] },
        chargers: { type: Object, default: null },
        plugs: { type: Object, default: null },
        history: { type: Array, default: () => [] },
        // Sessões ativas que já têm histórico gerado (shared/charges.js)
        inconsistencies: { type: Number, default: 0 },
        // Carregadores e plugs vêm calculados do backend e não respeitam os filtros da tela
        filtered: { type: Boolean, default: false },
    },

    computed: {
        allPoints() {
            return this.filtered ? " · todos os locais" : "";
        },
        tiles() {
            const charging = this.sessions.filter((s) => s.status == "Charging");
            const errors = this.sessions.filter((s) => s.errorSession).length;
            const power = charging.reduce((sum, s) => sum + (s.power > 0 ? s.power : 0), 0);
            const energySessions = this.sessions.reduce((sum, s) => sum + (s.consumption || 0), 0);
            const energyHistory = this.history.reduce((sum, h) => sum + (h.car ? h.car.consumption || 0 : 0), 0);
            const nfcSessions = this.sessions.filter((s) => s.unlockBy == UnlockBy.NFC).length;
            const nfcHistory = this.history.filter((h) => h.car && h.car.nfc).length;
            const paidSessions = this.sessions.filter(isPaid);
            const paidHistory = this.history.filter(isPaid);
            const charged = paidHistory.reduce((sum, h) => sum + (h.payment && h.payment.status == PaymentStatus.FINISH ? Number(h.payment.value) || 0 : 0), 0);
            const paymentAlerts = [...paidSessions.map(sessionPaymentAlerts), ...paidHistory.map(historyPaymentAlerts)].filter((alerts) => alerts.length > 0);
            const paymentCritical = paymentAlerts.filter((alerts) => alerts.some((a) => a.critical)).length;

            return [
                { icon: "ev_station", title: "Em andamento", value: this.sessions.length, subtitle: `${charging.length} carregando · ${nfcSessions} NFC` },
                {
                    icon: "error",
                    title: "Sessões com erro",
                    value: errors + this.inconsistencies,
                    subtitle: this.inconsistencies > 0 ? `${this.inconsistencies} com histórico já gerado` : errors > 0 ? "Necessita ação" : "Nenhum problema",
                    alert: errors + this.inconsistencies > 0,
                },
                { icon: "flash_on", title: "Potência atual", value: `${format.kW(power)} kW`, subtitle: "Soma das sessões carregando" },
                { icon: "history", title: "Finalizados", value: this.history.length, subtitle: `${nfcHistory} via NFC · ${paidHistory.length} pagos` },
                {
                    icon: "credit_card",
                    title: "Com pagamento",
                    value: paidSessions.length + paidHistory.length,
                    subtitle: paymentAlerts.length > 0 ? `${paymentAlerts.length} com alerta${paymentCritical > 0 ? ` (${paymentCritical} crítico${paymentCritical > 1 ? "s" : ""})` : ""}` : `${money(charged)} cobrados`,
                    alert: paymentCritical > 0,
                },
                { icon: "battery_charging_full", title: "Energia", value: `${(energyHistory + energySessions).toFixed(1)} kWh`, subtitle: `${energySessions.toFixed(1)} kWh em andamento` },
                {
                    icon: "router",
                    title: "Carregadores online",
                    value: this.chargers ? `${this.chargers.online}/${this.chargers.total}` : "-",
                    subtitle: this.chargers ? `${this.chargers.offline} sem comunicação${this.allPoints}` : "",
                },
                {
                    icon: "power",
                    title: "Plugs disponíveis",
                    value: this.plugs ? `${this.plugs.Available || 0}/${this.plugs.total}` : "-",
                    subtitle: this.plugs ? `${this.plugs.Charging || 0} carregando · ${this.plugs.Faulted || 0} em falha${this.allPoints}` : "",
                },
            ];
        },
    },
};
</script>

<style scoped>
.summary {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.summary-tile {
    flex: 1 1 0;
    min-width: 170px;
    padding: 12px 16px;
}

.summary-secondary {
    opacity: 0.7;
}
</style>
